/**
 * Common helpers shared by every schema in this package.
 */

import { z } from 'zod';

/**
 * UUID v4 primary key. Generated database-side via `gen_random_uuid()`
 * per ARCH-001 §2. Application-side IDs are never accepted on writes.
 */
export const uuidSchema = z.string().uuid();
export type Uuid = z.infer<typeof uuidSchema>;

/**
 * Timezone-aware ISO 8601 timestamp string. Postgres stores `timestamptz`
 * per ARCH-001 §5; the wire format is ISO 8601 UTC.
 */
export const isoTimestampSchema = z.string().datetime({ offset: true });
export type IsoTimestamp = z.infer<typeof isoTimestampSchema>;

/**
 * Email address. Validated via Zod's default RFC-5322 subset.
 */
export const emailSchema = z.string().email().max(320);

/**
 * Current JSONB payload schema versions. Sibling columns in Postgres are
 * `<column>_schema_version` per ARCH-001 §3. Bumping a number here is a
 * forward-compatible additive change; consumers branch on the version.
 *
 * When the shape of `context_doc.sections` or `deliverable.content` changes,
 * bump the matching constant below in the same change set as the migration
 * that introduces the new column or sub-field.
 */
export const JSONB_SCHEMA_VERSIONS = {
  contextDocSections: 1,
  deliverableContent: 1,
  progressionReportContent: 1,
  notificationPayload: 1,
} as const;

export type JsonbSchemaVersion =
  (typeof JSONB_SCHEMA_VERSIONS)[keyof typeof JSONB_SCHEMA_VERSIONS];

/**
 * Versioned payload wrapper. Use to brand any JSONB-backed payload with the
 * schema version that produced it. Read paths can switch on `_schema_version`.
 */
export function versionedSchema<T extends z.ZodTypeAny>(
  payload: T,
  version: number,
) {
  return z.object({
    _schema_version: z.literal(version),
  }).and(payload);
}
